import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Users as UsersIcon,
  UserPlus,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { getTenants } from "@/api/endpoints/tenants";
import {
  createTenantUser,
  listTenantUsers,
  deleteUser,
  setUserEnabled,
  resetUserPassword,
  type CreateUserPayload,
} from "@/api/endpoints/adminUsers";
import { Key } from "lucide-react";
import PageHelp from "@/components/Helper/PageHelp";
import { openHelper } from "@/components/Helper/HelperWidget";

function generatePassword(len = 16): string {
  const charset =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => charset[n % charset.length]).join("");
}

export default function AdminUsers() {
  const queryClient = useQueryClient();

  const tenantsQuery = useQuery({ queryKey: ["tenants"], queryFn: getTenants });

  const [tenantId, setTenantId] = useState<number | "">("");
  const [form, setForm] = useState<CreateUserPayload>({
    email: "",
    username: "",
    first_name: "",
    last_name: "",
    tenant_id: 0,
    temporary_password: generatePassword(),
  });

  const usersQuery = useQuery({
    queryKey: ["admin-users", tenantId],
    queryFn: () => listTenantUsers(tenantId as number),
    enabled: typeof tenantId === "number",
  });

  const createMut = useMutation({
    mutationFn: (p: CreateUserPayload) => createTenantUser(p),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setForm({
        email: "",
        username: "",
        first_name: "",
        last_name: "",
        tenant_id: typeof tenantId === "number" ? tenantId : 0,
        temporary_password: generatePassword(),
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setUserEnabled(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const [resetPwd, setResetPwd] = useState<{
    userId: string;
    username: string;
    password: string;
  } | null>(null);

  const resetMut = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      resetUserPassword(id, password),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (typeof tenantId !== "number") return;
    createMut.mutate({ ...form, tenant_id: tenantId });
  };

  const selectedTenant = tenantsQuery.data?.find(
    (t) => Number(t.id) === tenantId,
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <UsersIcon className="h-7 w-7 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Utilisateurs</h1>
            <p className="text-sm text-gray-400">
              Cree un user Keycloak et l'attache au groupe d'un tenant.
            </p>
          </div>
        </div>
        <PageHelp onAskHelper={openHelper} />
      </div>

      {/* Tenant picker */}
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-500">
          Tenant cible
        </label>
        <select
          value={tenantId}
          onChange={(e) =>
            setTenantId(e.target.value ? Number(e.target.value) : "")
          }
          className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
        >
          <option value="">— Choisir un tenant —</option>
          {(tenantsQuery.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.slug}){" "}
              {t.keycloak_group_id ? "" : "[no Keycloak group]"}
            </option>
          ))}
        </select>
        {selectedTenant && !selectedTenant.keycloak_group_id && (
          <p className="mt-2 flex items-center gap-2 text-sm text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            Ce tenant n'a pas de groupe Keycloak — la creation echouera.
            Re-execute le mappage manuel ou re-create le tenant.
          </p>
        )}
      </section>

      {/* Create form */}
      {typeof tenantId === "number" && (
        <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-200">
            <UserPlus className="h-4 w-4" /> Nouvel utilisateur
          </h2>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Username"
                value={form.username}
                onChange={(v) => setForm({ ...form, username: v })}
                required
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                required
              />
              <Field
                label="Prenom"
                value={form.first_name ?? ""}
                onChange={(v) => setForm({ ...form, first_name: v })}
              />
              <Field
                label="Nom"
                value={form.last_name ?? ""}
                onChange={(v) => setForm({ ...form, last_name: v })}
              />
            </div>
            <div>
              <label className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-gray-500">
                Mot de passe temporaire (l'utilisateur devra le changer)
                <button
                  type="button"
                  onClick={() =>
                    setForm({ ...form, temporary_password: generatePassword() })
                  }
                  className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                >
                  <RefreshCw className="h-3 w-3" /> regenerer
                </button>
              </label>
              <input
                type="text"
                value={form.temporary_password}
                onChange={(e) =>
                  setForm({ ...form, temporary_password: e.target.value })
                }
                className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 font-mono text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
              />
            </div>
            {createMut.isError && (
              <p className="flex items-center gap-2 text-sm text-red-400">
                <AlertTriangle className="h-4 w-4" />{" "}
                {(createMut.error as Error).message}
              </p>
            )}
            {createMut.isSuccess && (
              <p className="flex items-center gap-2 text-sm text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> User{" "}
                <span className="font-mono">{createMut.data?.username}</span>{" "}
                cree (kc_id={createMut.data?.user_id?.slice(0, 8)}...)
              </p>
            )}
            <button
              type="submit"
              disabled={
                !form.email ||
                !form.username ||
                !form.temporary_password ||
                createMut.isPending
              }
              className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Creer l'utilisateur
            </button>
          </form>
        </section>
      )}

      {/* Reset-password modal */}
      {resetPwd && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setResetPwd(null)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-900 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-200">
              <Key className="h-4 w-4" /> Mot de passe temporaire pour{" "}
              <span className="font-mono">{resetPwd.username}</span>
            </h3>
            <p className="mb-3 text-xs text-gray-400">
              A transmettre en canal securise. L'utilisateur devra le changer
              a la premiere connexion (flag temporary=true).
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-gray-950 px-3 py-2 font-mono text-sm text-gray-100">
                {resetPwd.password}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(resetPwd.password);
                }}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-xs text-gray-200 hover:bg-gray-700"
              >
                Copier
              </button>
            </div>
            {resetMut.isError && (
              <p className="mt-2 flex items-center gap-2 text-sm text-red-400">
                <AlertTriangle className="h-4 w-4" />{" "}
                {(resetMut.error as Error).message}
              </p>
            )}
            {resetMut.isSuccess && (
              <p className="mt-2 flex items-center gap-2 text-sm text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> Reset applique cote Keycloak
              </p>
            )}
            <button
              onClick={() => setResetPwd(null)}
              className="mt-4 w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
            >
              J'ai copie, fermer
            </button>
          </div>
        </div>
      )}

      {/* Existing users */}
      {typeof tenantId === "number" && (
        <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-200">
            Utilisateurs du tenant
            {usersQuery.data && (
              <span className="ml-2 text-gray-500">
                ({usersQuery.data.users.length})
              </span>
            )}
          </h2>
          {usersQuery.isLoading && (
            <p className="text-sm text-gray-500">Chargement...</p>
          )}
          {usersQuery.error && (
            <p className="flex items-center gap-2 text-sm text-red-400">
              <AlertTriangle className="h-4 w-4" />{" "}
              {(usersQuery.error as Error).message}
            </p>
          )}
          {usersQuery.data && usersQuery.data.users.length === 0 && (
            <p className="text-sm text-gray-500">
              Aucun utilisateur dans ce tenant.
            </p>
          )}
          {usersQuery.data && usersQuery.data.users.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">Username</th>
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Nom</th>
                  <th className="py-2 pr-4 font-medium">Statut</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersQuery.data.users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-gray-800/50 text-gray-300"
                  >
                    <td className="py-2 pr-4 font-mono text-xs">
                      {u.username}
                    </td>
                    <td className="py-2 pr-4 text-xs">{u.email}</td>
                    <td className="py-2 pr-4 text-xs">
                      {u.first_name} {u.last_name}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      <span
                        className={
                          u.enabled
                            ? "rounded bg-emerald-900/40 px-2 py-0.5 text-emerald-300"
                            : "rounded bg-gray-800 px-2 py-0.5 text-gray-500"
                        }
                      >
                        {u.enabled ? "actif" : "desactive"}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => {
                            const pwd = generatePassword();
                            setResetPwd({
                              userId: u.id,
                              username: u.username,
                              password: pwd,
                            });
                            resetMut.mutate({ id: u.id, password: pwd });
                          }}
                          disabled={
                            resetMut.isPending && resetMut.variables?.id === u.id
                          }
                          title="Generer un mot de passe temporaire (user devra le changer a la 1ere connexion)"
                          className="inline-flex items-center gap-1 rounded border border-blue-900/50 bg-blue-900/20 px-2 py-1 text-xs text-blue-300 hover:bg-blue-900/40 disabled:opacity-50"
                        >
                          {resetMut.isPending && resetMut.variables?.id === u.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Key className="h-3 w-3" />
                          )}
                        </button>
                        <button
                          onClick={() =>
                            toggleMut.mutate({ id: u.id, enabled: !u.enabled })
                          }
                          disabled={
                            toggleMut.isPending &&
                            toggleMut.variables?.id === u.id
                          }
                          title={
                            u.enabled
                              ? "Desactiver l'utilisateur"
                              : "Reactiver l'utilisateur"
                          }
                          className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-50 ${
                            u.enabled
                              ? "border-amber-900/50 bg-amber-900/20 text-amber-300 hover:bg-amber-900/40"
                              : "border-emerald-900/50 bg-emerald-900/20 text-emerald-300 hover:bg-emerald-900/40"
                          }`}
                        >
                          {toggleMut.isPending &&
                          toggleMut.variables?.id === u.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : u.enabled ? (
                            "desactiver"
                          ) : (
                            "activer"
                          )}
                        </button>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                `Supprimer l'utilisateur "${u.username}" ? Action irreversible.`,
                              )
                            ) {
                              deleteMut.mutate(u.id);
                            }
                          }}
                          disabled={
                            deleteMut.isPending && deleteMut.variables === u.id
                          }
                          title="Supprimer l'utilisateur"
                          className="inline-flex items-center gap-1 rounded border border-red-900/50 bg-red-900/20 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40 disabled:opacity-50"
                        >
                          {deleteMut.isPending && deleteMut.variables === u.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wide text-gray-500">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
      />
    </div>
  );
}
